import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useModal } from '@private/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InternalAxiosRequestConfig } from 'axios';

import api from 'api';
import { AUTH_REQUEST } from 'api/auth/constants';
import { IAuthState, ILoginPayload } from 'api/auth/types';
import { TOKEN_INSTANCES } from 'api/base';
import { STATUSES } from 'constants/common';
import { useAppDispatch, useAppSelector } from 'hooks/storeHooks';
import { defaultMutationThrowOnError } from 'queries';
import {
  getTransactionReportingPeriod,
  isIdleExpired,
  logoutWithQuery,
  updateConfigHeader,
  updateRequestHeaders,
} from 'queries/session/utils';
import { useQueryData } from 'queries/utils';
import { ROUTES } from 'routes/config/constants';
import { setIsAuthenticated } from 'store/session';
import {
  resetUserConfiguration,
  setIsLeaderTab,
  setTransactionReportingPeriod,
  setTransactionReportingTimezone,
  utcTimeZone,
} from 'store/userConfiguration';
import {
  BROADCAST_CHANNEL_APP_ACTION,
  channel,
  leaderTabElector,
} from 'utils/BroadcastChannel';
import { sleep } from 'utils/common';
import { ReloginRedirect } from 'utils/reloginRedirect';
import { QueryError } from 'utils/types';

import { SESSION_CONFIG, SESSION_KEYS } from './constants';

interface ILogoutResult {
  logout: (data?: boolean) => Promise<unknown>;
  logoutApp: (data?: boolean) => void;
}

export const useLogout = (): ILogoutResult => {
  const navigate = useNavigate();
  const { closeModal } = useModal();
  const client = useQueryClient();
  const dispatch = useAppDispatch();

  const logoutApp = (context?: boolean) => {
    updateRequestHeaders();
    logoutWithQuery(client);
    dispatch(setIsAuthenticated(false));
    closeModal();
    if (context) {
      navigate(ROUTES.SIGN_IN.PATH);
    }
    dispatch(resetUserConfiguration());
  };

  const { mutateAsync: logout } = useMutation({
    mutationFn: api.auth.logout,
    onSettled: (_data, _error, context = true) => {
      logoutApp(context);
      channel.postMessage({ type: BROADCAST_CHANNEL_APP_ACTION.LOGOUT });
    },
    throwOnError: false,
  });

  return { logout, logoutApp };
};

export const useAuthentication = () => {
  const navigate = useNavigate();
  const client = useQueryClient();
  const dispatch = useAppDispatch();
  const session = useQueryData<IAuthState>([SESSION_KEYS.SESSION]);
  const isInitialized = useQueryData<boolean>([SESSION_KEYS.IS_INITIALIZED]);
  const isThemeRequested = useQueryData<boolean>([SESSION_KEYS.IS_THEME_REQUESTED]);
  const isAuthenticated = useAppSelector((state) => state.session.isAuthenticated);
  const { logout, logoutApp } = useLogout();

  const loginApp = useCallback(
    (response: IAuthState, isAnotherTabLoginTrigger?: boolean) => {
      updateRequestHeaders(response.token);
      dispatch(setIsAuthenticated(true));
      if (!isAnotherTabLoginTrigger) {
        dispatch(setTransactionReportingPeriod(getTransactionReportingPeriod()));
        dispatch(setTransactionReportingTimezone({ timeZone: utcTimeZone }));
      }
      client.setQueryData([SESSION_KEYS.SESSION], response);

      const { lastLoginPath, lastLoginUserId } = ReloginRedirect.getLastSessionData();

      ReloginRedirect.setSessionUserId(response.id);

      if (lastLoginUserId === response.id && lastLoginPath) {
        setTimeout(() => navigate(lastLoginPath), 0);
      } else if (!lastLoginUserId && lastLoginPath) {
        setTimeout(() => navigate(lastLoginPath), 0);
      }
    },
    [client, dispatch, navigate],
  );

  useEffect(() => {
    channel.onmessage = (event) => {
      if (isAuthenticated && event.type === BROADCAST_CHANNEL_APP_ACTION.LOGOUT) {
        logoutApp();
      }

      if (event.type === BROADCAST_CHANNEL_APP_ACTION.LOGIN) {
        loginApp(event.payload, true);
      }
    };

    return () => {
      channel.onmessage = null;
    };
  }, [isAuthenticated]);

  const {
    error: loginError,
    isPending: isLoadingLogin,
    mutate: login,
  } = useMutation<IAuthState, QueryError, ILoginPayload>({
    mutationFn: api.auth.signIn,
    onSuccess: (response) => {
      loginApp(response);
      channel.postMessage({
        payload: response,
        type: BROADCAST_CHANNEL_APP_ACTION.LOGIN,
      });
    },
    throwOnError: (error) =>
      error.response?.status !== STATUSES.TOO_MANY_REQUESTS &&
      defaultMutationThrowOnError(error),
  });

  return {
    isAuthenticated,
    isAuthorized: !!session?.token,
    isInitialized: !!isInitialized,
    isLoadingLogin,
    isThemeRequested: !!isThemeRequested,
    login,
    loginError,
    logout,
  };
};

export const useSessionRecover = (): void => {
  const isRefreshing = useRef<boolean>(false);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const isLeaderTab = useAppSelector((state) => state.userConfiguration.isLeaderTab);
  const isAuthenticated = useAppSelector((state) => state.session.isAuthenticated);
  const { logout, logoutApp } = useLogout();
  const location = useLocation();
  const session = useQueryData<IAuthState>([SESSION_KEYS.SESSION]);

  const isSetPasswordPage = location.pathname === ROUTES.SET_PASSWORD.PATH;

  const expiresIn = session?.expiration ? session.expiration * 1000 : 0; // conversion of expiration from seconds to milliseconds

  const expiresInWithFallback = expiresIn || SESSION_CONFIG.EXPIRATION_OFFSET;

  // time in milliseconds when token is valid before next refetch
  const refetchInterval =
    expiresInWithFallback > SESSION_CONFIG.EXPIRATION_OFFSET
      ? expiresInWithFallback - SESSION_CONFIG.EXPIRATION_OFFSET
      : SESSION_CONFIG.EXPIRATION_OFFSET;

  const refreshToken = async () => {
    // An idle session must not be extended; useIdleLogout ends it on its next tick
    if (isIdleExpired() && session) {
      return session;
    }

    isRefreshing.current = true;
    try {
      return await api.auth.refreshToken();
    } finally {
      isRefreshing.current = false;
    }
  };

  const {
    data: response,
    isError: isRefreshTokenError,
    isFetched: isRefreshTokenFetched,
    isSuccess: isRefreshTokenSuccess,
    refetch: doRefreshToken,
  } = useQuery<IAuthState>({
    enabled:
      (!session?.token || isLeaderTab) &&
      (isSetPasswordPage ? false : isAuthenticated) &&
      !isIdleExpired(),
    gcTime: Infinity,
    queryFn: refreshToken,
    queryKey: [SESSION_KEYS.SESSION],
    refetchInterval, // example: refetch after each (token expiration time ms - 30000 ms)
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: Infinity,
    throwOnError: false,
  });

  useEffect(() => {
    leaderTabElector.awaitLeadership().then(() => {
      dispatch(setIsLeaderTab({ isLeaderTab: true }));
    });

    leaderTabElector.onduplicate = () => {
      leaderTabElector.die();
      leaderTabElector.applyOnce();
      dispatch(setIsLeaderTab({ isLeaderTab: false }));
    };
  }, []);

  useEffect(() => {
    if (isRefreshTokenSuccess && response?.token) {
      queryClient.setQueryData([SESSION_KEYS.IS_LOGGED_IN], true);
      dispatch(setIsAuthenticated(true));
      updateRequestHeaders(response.token);
      channel.postMessage({
        payload: response,
        type: BROADCAST_CHANNEL_APP_ACTION.LOGIN,
      });
    }
  }, [dispatch, isRefreshTokenSuccess, queryClient, response]);

  useEffect(() => {
    if (isRefreshTokenError) {
      logout();
    }
  }, [isRefreshTokenError, response, logout]);

  useEffect(() => {
    if (isRefreshTokenFetched) {
      const isAlreadyInitialized = queryClient.getQueryData([
        SESSION_KEYS.IS_INITIALIZED,
      ]);
      if (!isAlreadyInitialized) {
        queryClient.setQueryData([SESSION_KEYS.IS_INITIALIZED], true);
      }
    }
  }, [isRefreshTokenFetched, response, queryClient]);

  const requestInterceptor = async (config: InternalAxiosRequestConfig) => {
    if (config.authRequest) {
      return config;
    }

    const session = queryClient.getQueryData<IAuthState>([SESSION_KEYS.SESSION]);

    if (!session) {
      return config;
    }

    const expiresIn = session.expirationDate; // expiration date in ms

    const isTokenValid = expiresIn > Date.now() + SESSION_CONFIG.EXPIRATION_OFFSET;

    if (isTokenValid || isIdleExpired()) {
      return config;
    }

    if (!isRefreshing.current) {
      isRefreshing.current = true;
      try {
        const { data } = await doRefreshToken();
        return updateConfigHeader(config, data?.token || '');
      } finally {
        isRefreshing.current = false;
      }
    }

    let ticks = 0;
    while (
      isRefreshing.current &&
      ticks < SESSION_CONFIG.PENDING_TIMEOUT / SESSION_CONFIG.PENDING_TICKER_STEP
    ) {
      await sleep(SESSION_CONFIG.PENDING_TICKER_STEP);
      ticks += 1;
    }

    const updatedSession = queryClient.getQueryData<IAuthState>([SESSION_KEYS.SESSION]);
    return updateConfigHeader(config, updatedSession?.token || '');
  };

  // eslint-disable-next-line
  const errorInterceptor = async (error: any) => {
    const isTokenFailed = error?.response?.status === STATUSES.AUTHENTICATION;
    const isInvalidData = error?.response?.status === STATUSES.INVALID_DATA;
    const authRequest: AUTH_REQUEST | undefined = error?.config?.authRequest;
    const isRefreshRequest = authRequest === AUTH_REQUEST.REFRESH;

    if (isRefreshRequest && isInvalidData) {
      logoutApp(true);
      return Promise.reject(error);
    }

    if (authRequest || isRefreshing.current) {
      return Promise.reject(error);
    }

    if (isTokenFailed) {
      logout();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  };

  const initializeApp = async () => {
    if (isAuthenticated && (isSetPasswordPage || isIdleExpired())) {
      try {
        await logout(false);
      } catch {
        // onSettled has already logged out locally; the app must still initialize
      }
      queryClient.setQueryData([SESSION_KEYS.IS_INITIALIZED], true);
    } else if (!isAuthenticated) {
      const isAlreadyInitialized = queryClient.getQueryData([
        SESSION_KEYS.IS_INITIALIZED,
      ]);
      if (!isAlreadyInitialized) {
        queryClient.setQueryData([SESSION_KEYS.IS_INITIALIZED], true);
      }
    }

    const ejectInterceptors = TOKEN_INSTANCES.map((instance) => {
      const requestId = instance.interceptors.request.use(requestInterceptor);
      const responseId = instance.interceptors.response.use(
        (response) => response,
        errorInterceptor,
      );

      return () => {
        instance.interceptors.request.eject(requestId);
        instance.interceptors.response.eject(responseId);
      };
    });

    return () => {
      ejectInterceptors.forEach((eject) => eject());
    };
  };

  useEffect(() => {
    initializeApp();
  }, []);
};
